import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import type {
  ImagingResultCreateFromTemplatePayload,
  ImagingResultResponse,
  PatientImagingResult
} from '../types/imaging_results.type'

const JSON_API = { headers: { 'Content-Type': 'application/vnd.api+json' } }

type CatalogImagingResultCreatePayload =
  | ImagingResultCreateFromTemplatePayload
  | {
      data: {
        type: 'imaging_result'
        attributes: {
          attachment_signed_ids: string[]
          study_name?: string
        }
      }
    }

export const useImagingResult = (orderId?: string, resultId?: string) => {
  const invalidateResults = () => {
    queryClient.invalidateQueries({ queryKey: ['imaging_result'] })
    queryClient.invalidateQueries({ queryKey: ['orders'] })
    queryClient.invalidateQueries({ queryKey: ['imaging_order'] })
  }

  const { mutateAsync: updateImagingResult, isPending: isUpdatingImagingResult } = useMutation({
    mutationFn: async ({ formVal, resultId }: { formVal: any; resultId: string }) => {
      if (!resultId) throw new Error('resultId is required')
      const isJsonApi = formVal?.data?.type === 'imaging_result'
      const { data } = await AXIOS_INSTANCE.patch(`/imaging_results/${resultId}`, formVal, {
        headers: {
          'Content-Type': isJsonApi ? 'application/vnd.api+json' : 'multipart/form-data'
        }
      })
      return data as ImagingResultResponse
    },
    onSuccess: (_data, { resultId: rid }) => {
      queryClient.invalidateQueries({ queryKey: ['imaging_result', { resultId: rid }] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['imaging_order'] })
    }
  })

  const { mutateAsync: deleteImagingResult, isPending: isDeletingImagingResult } = useMutation({
    mutationFn: async (resultId: string) => {
      if (!resultId) throw new Error('resultId is required')
      await AXIOS_INSTANCE.delete(`/imaging_results/${resultId}`)
    },
    onSuccess: () => invalidateResults()
  })

  /** Catalog child line: `POST /orders/:order_id/imaging_results` (template or attachment). */
  const { mutateAsync: createCatalogImagingResult } = useMutation({
    mutationFn: async ({ formVal, order_id }: { formVal: CatalogImagingResultCreatePayload; order_id: string }) => {
      if (!order_id) throw new Error('order_id is required')
      const { data } = await AXIOS_INSTANCE.post(`/orders/${order_id}/imaging_results`, formVal, JSON_API)
      return data as ImagingResultResponse
    },
    onSuccess: (data, { order_id }) => {
      const resultId = data?.data?.id
      if (resultId) {
        queryClient.setQueryData(['imaging_result', { resultId }], data)
        queryClient.setQueryData(['orders', { id: order_id }], {
          data: {
            id: order_id,
            type: 'order',
            attributes: {},
            relationships: {
              imaging_result: { data: { type: 'imaging_result', id: String(resultId) } }
            },
            meta: { status: 'in_progress' }
          },
          included: [
            {
              ...data.data,
              relationships: {
                ...data.data.relationships,
                order: { data: { type: 'order', id: order_id } }
              }
            }
          ]
        })
      }
      // Refetch parent/child shows so cold-load payloads match the seed after backend includes.
      invalidateResults()
    }
  })

  const {
    data: imagingResult,
    refetch: refetchImagingResult,
    isLoading: isImagingResultLoading
  } = useQuery({
    enabled: !!resultId,
    queryFn: async () => {
      if (!resultId) return null
      const { data } = await AXIOS_INSTANCE.get(`/imaging_results/${resultId}`)
      return data as ImagingResultResponse
    },
    queryKey: ['imaging_result', { resultId }]
  })

  return {
    createCatalogImagingResult,
    deleteImagingResult,
    isDeletingImagingResult,
    imagingResult,
    isImagingResultLoading,
    isUpdatingImagingResult,
    refetchImagingResult,
    updateImagingResult
  }
}

export interface UseImagingResultsArgs {
  encounterId?: string
  patientId?: string
  query?: string
  limit?: string
  page?: string
  sortDir?: string
  sortBy?: string
  lockedDate?: string
  imagingStudyType?: string
  interpretation?: string
}

export const useImagingResults = (arg: UseImagingResultsArgs) => {
  const { encounterId, patientId, query, limit, page, sortDir, sortBy, lockedDate, imagingStudyType, interpretation } =
    arg

  const { data, isLoading } = useQuery<PatientImagingResult | undefined>({
    queryFn: async () => {
      let endpoint = `/imaging_results?`
      if (patientId) endpoint += `patient_id=${patientId}&`
      if (encounterId) endpoint += `encounter_id=${encounterId}&`
      if (sortDir) endpoint += `sort_dir=${sortDir}&`
      if (sortBy) endpoint += `sort_by=${sortBy}&`
      if (query) endpoint += `query=${query}&`
      if (limit) endpoint += `limit=${limit}&`
      if (interpretation) endpoint += `interpretation=${interpretation}&`
      if (imagingStudyType) endpoint += `imaging_study_type[]=${imagingStudyType}`
      if (page) endpoint += `page%5Bnumber%5D=${page}&page%5Bsize%5D=${limit}&`
      if (lockedDate) endpoint += `locked_date=${lockedDate}&`
      endpoint = endpoint.replace(/[&?]$/, '')
      const { data } = await AXIOS_INSTANCE.get<PatientImagingResult>(endpoint)
      return data
    },
    queryKey: [
      'imaging_results',
      { encounterId, interpretation, limit, lockedDate, page, patientId, query, sortBy, sortDir }
    ]
  })

  return {
    imagingResults: data,
    included: data?.included,
    isLoading
  }
}

export default useImagingResult
